import React, { useContext, useEffect, useReducer, useCallback, useState } from 'react';
import { scheduleViewReducer } from './scheduleViewReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from 'modules/CustomersAndRestaurants/Restaurants/RestaurantFullInfo/restaurantFullInfoState/restaurantFullInfoContext';
import {
	ScheduleViewContext,
	scheduleViewInitialState,
	TScheduleViewCountOfItems,
	TScheduleViewSortBy,
	TTypeOfScheduleViewModalWindow,
} from './scheduleViewContext';
import { ScheduleViewActionTypes } from '../scheduleViewActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedScheduledPushes, deleteScheduledPush } from 'backend/pushes';
import { AxiosResponse } from 'axios';
import { IScheduledPushes, IGetPaginatedScheduledPushes, TScheduledPushStatus } from 'types';

const ScheduleViewState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(scheduleViewReducer, scheduleViewInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantForFullInfo } = useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const { refetch, response, loading } = useFetchData<AxiosResponse<IScheduledPushes>, IGetPaginatedScheduledPushes>(
		restaurantForFullInfo?.id ? getPaginatedScheduledPushes : null,
		{
			customerId: restaurantForFullInfo?.customerId || '',
			restaurantIds: [restaurantForFullInfo?.id || ''],
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			status: state.status,
			sortByType: state.sortBy === 'PushesA-Z' || state.sortBy === 'PushesZ-A' ? state.sortBy : null,
			sortByScheduledDateTime:
				state.sortBy === 'Date/Time1-10' || state.sortBy === 'Date/Time10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ScheduleViewActionTypes.SET_SCHEDULED_PUSHES,
				payload: {
					scheduledPushes: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteScheduledPush(state.IDForDeletion);
			const filteredScheduledPushes = state.scheduledPushes.pushNotifications.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: ScheduleViewActionTypes.SET_SCHEDULED_PUSHES,
				payload: {
					scheduledPushes: {
						count: state.scheduledPushes.count - 1,
						pushNotifications: filteredScheduledPushes,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: ScheduleViewActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.scheduledPushes]);

	const refetchSchedules = (value: boolean) => {
		dispatch({
			type: ScheduleViewActionTypes.REFETCH_SCHEDULES,
			payload: {
				needRefetchSchedules: value,
			},
		});
	};

	const changeSearchString = (value: string) => {
		dispatch({
			type: ScheduleViewActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const setStatus = (value: TScheduledPushStatus | null) => {
		dispatch({
			type: ScheduleViewActionTypes.SET_STATUS,
			payload: {
				status: value,
			},
		});
	};

	const setSortBy = (sortBy: TScheduleViewSortBy) => {
		dispatch({
			type: ScheduleViewActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TScheduleViewCountOfItems) => {
		dispatch({
			type: ScheduleViewActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: ScheduleViewActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: ScheduleViewActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForEdit = (value: string) => {
		dispatch({
			type: ScheduleViewActionTypes.SET_ID_FOR_EDIT,
			payload: {
				IDForEdit: value,
				typeOfModalWindow: 'editSchedule',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfScheduleViewModalWindow) => {
		dispatch({
			type: ScheduleViewActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	useEffect(() => {
		if (isFirstRender) {
			setIsFirstRender(false);
			return;
		}
		refetch();
	}, [
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.sortBy,
		state.status,
		restaurantForFullInfo?.id,
	]);

	useEffect(() => {
		if (state.needRefetchSchedules) {
			refetch();
			refetchSchedules(false);
		}
	}, [state.needRefetchSchedules]);

	return (
		<ScheduleViewContext.Provider
			value={{
				deleteSelectedItem,
				refetchSchedules,
				changeSearchString,
				setStatus,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForEdit,
				changeTypeOfModalWindow,
				scheduledPushes: state.scheduledPushes,
				needRefetchSchedules: state.needRefetchSchedules,
				searchString: state.searchString,
				status: state.status,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForEdit: state.IDForEdit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ScheduleViewContext.Provider>
	);
};

export default ScheduleViewState;
